/**
 * Progress content for provider/facility-facing outreach.
 * Captures contact method (with provider-specific options), outcome, and notes.
 * @agentic connections
 */

import { Stack, Text, Textarea, Button, Alert, Select } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useState, useCallback, useMemo, useEffect } from "react";
import {
  PROVIDER_COMMUNICATION_MEDIUM_SELECT_OPTIONS,
  PROVIDER_OUTREACH_OUTCOMES,
} from "@benchmark/core/constants";
import type { ProgressContentProps } from "@/types/domain/progress-log";
import type { ProgressContactMethod } from "@benchmark/core/types";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { CareTimeActivityDurationField } from "@/components/domain/care-time/CareTimeActivityDurationField";

/**
 * Provider outreach progress form for provider/facility-facing contact attempts.
 * @agentic connections
 */
export const ProviderOutreachProgressContent = ({
  onSubmit,
  onCancel,
  onDirtyChange,
}: ProgressContentProps) => {
  const [contactMethod, setContactMethod] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [careMinutes, setCareMinutes] = useState<number | undefined>(undefined);

  const isDirty =
    !!contactMethod || !!outcome || notes.trim() !== "" || !!careMinutes;
  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  const outcomeOptions = useMemo(
    () =>
      PROVIDER_OUTREACH_OUTCOMES.map((o) => ({
        value: o.value,
        label: o.label,
      })),
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!contactMethod || !outcome) return;

    const selectedOutcome = PROVIDER_OUTREACH_OUTCOMES.find(
      (o) => o.value === outcome,
    );
    if (!selectedOutcome) return;

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        contactMethod: contactMethod as ProgressContactMethod,
        outcome: selectedOutcome.value,
        outcomeDisplay: selectedOutcome.label,
        notes: notes || undefined,
        snoozeBehavior: selectedOutcome.snoozeBehavior,
        minutesLogged: careMinutes && careMinutes > 0 ? careMinutes : undefined,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to log provider outreach",
      );
    } finally {
      setSubmitting(false);
    }
  }, [contactMethod, outcome, notes, onSubmit, careMinutes]);

  const canSubmit = !!contactMethod && !!outcome;

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <Text size="sm">Log a provider or facility outreach attempt.</Text>

      <Select
        label="Contact Method"
        placeholder="Select method..."
        required
        data={PROVIDER_COMMUNICATION_MEDIUM_SELECT_OPTIONS}
        value={contactMethod}
        onChange={setContactMethod}
      />

      <CardSelect
        placeholder="Select outcome"
        value={outcome}
        onChange={(value) => {
          const singleValue = Array.isArray(value) ? value[0] : value;
          setOutcome(singleValue || "");
        }}
        data={outcomeOptions}
        columns={2}
      />

      <Textarea
        label="Notes (optional)"
        placeholder="Add details about the provider outreach..."
        value={notes}
        onChange={(e) => setNotes(e.currentTarget.value)}
        autosize
        minRows={2}
        maxRows={4}
      />

      <CareTimeActivityDurationField
        minutes={careMinutes}
        onMinutesChange={setCareMinutes}
      />

      <Stack gap="xs">
        <Button
          onClick={handleSubmit}
          loading={submitting}
          disabled={!canSubmit}
          color="purple"
          fullWidth
        >
          Log Provider Outreach
        </Button>
        <Button
          variant="subtle"
          onClick={onCancel}
          disabled={submitting}
          fullWidth
        >
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};
